import { createFileRoute, Link } from '@tanstack/react-router';
import { InputOTPForm } from '@workspace/ui/components/input-otp-form';
import { useEffect, useState } from 'react';
import type { OriginName } from '~/interfaces/common';
import { m } from '~/paraglide/messages.js';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '~/utils/local-storage';
import { initiateAuthentication } from '../login/-api/api';
import { verifyOtpCode } from './-api';

export const Route = createFileRoute('/auth/otp/$origin-name')({
  validateSearch: (
    search: Record<string, string>,
  ): { redirect?: string; alias: string } => {
    return {
      redirect: search.redirect,
      alias: search.alias,
    };
  },
  component: OtpVerification,
});

function OtpVerification() {
  const { 'origin-name': originName } = Route.useParams();
  const { redirect, alias } = Route.useSearch();

  const providerName = `bloque-${originName}` as OriginName;

  const [isVerifying, setIsVerifying] = useState(false);

  const authFlow =
    getLocalStorageItem<'login' | 'signup'>('authFlow') || 'login';

  const handleResend = async () => {
    await initiateAuthentication(providerName, alias);
  };

  const handleOtpSubmit = async (otp: string) => {
    if (!otp.trim()) return;

    setIsVerifying(true);
    await verifyOtpCode(providerName, alias, otp);
    setIsVerifying(false);
  };

  useEffect(() => {
    if (redirect) {
      setLocalStorageItem('redirect', redirect);
    }
  }, []);

  return (
    <div className="flex items-center justify-center min-h-full">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-primary-900">
            {m['auth.otp.title']({ originName })}
          </h1>
          <p className="font-normal text-sm text-muted-foreground">
            {m['auth.otp.subtitle']()}
          </p>
        </div>
        <div className="space-y-4 border rounded-xl p-8">
          <div className="flex items-center justify-center gap-2">
            <div className="text-base text-white font-semibold">
              {originName === 'whatsapp' && '+'}
              {alias}
            </div>
            <Link
              to={`/auth/${authFlow}`}
              search={{ redirect }}
              className="text-sm text-primary-900 hover:text-primary-700 underline font-normal"
            >
              {originName === 'whatsapp'
                ? m['auth.otp.change_number']()
                : m['auth.otp.change_email']()}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <InputOTPForm
              onSubmit={handleOtpSubmit}
              isLoading={isVerifying}
              onResend={handleResend}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
